import React, { Fragment, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { useTranslation } from 'react-i18next';
import { L10n } from '@syncfusion/ej2-base';
import 'bootstrap/dist/css/bootstrap.css';
import MainSpinner from 'shared/components/MainSpinner';
import '../../scss/app.scss';
import Router from './Router';
import { store, persistor } from './store';
import ScrollToTop from './ScrollToTop';

const App = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // const foo = {
  //  bar: 'baz',
  //  qux: 'quux',
  // };

  // Load Locale text to FileManager component
  L10n.load({
    es: {
      filemanager: {
        NewFolder: t('documents_new_folder'),
        Upload: t('general_upload'),
        Delete: t('documents_delete'),
        Rename: t('documents_rename'),
        Download: t('documents_download'),
        Cut: t('documents_cut'),
        Copy: t('documents_copy'),
        Paste: t('documents_paste'),
        SortBy: t('general_order_by'),
        Refresh: t('general_refresh'),
        'Item-Selection': t('documents_item_selection'),
        'Items-Selection': t('documents_items_selection'),
        View: t('general_see'),
        Details: t('documents_details'),
        SelectAll: t('general_select_all'),
        Open: t('documents_open'),
        'Tooltip-NewFolder': t('documents_new_folder'),
        'Tooltip-Upload': t('general_upload'),
        'Tooltip-Delete': t('documents_delete'),
        'Tooltip-Rename': t('documents_rename'),
        'Tooltip-Download': t('documents_download'),
        'Tooltip-Cut': t('documents_cut'),
        'Tooltip-Copy': t('documents_copy'),
        'Tooltip-Paste': t('documents_paste'),
        'Tooltip-SortBy': t('general_order_by'),
        'Tooltip-Refresh': t('general_refresh'),
        'Tooltip-Selection': t('documents_deselect'),
        'Tooltip-View': t('general_see'),
        'Tooltip-Details': t('documents_details'),
        'Tooltip-SelectAll': t('general_select_all'),
        Name: t('general_name'),
        Size: t('general_size'),
        DateModified: t('general_date_modified'),
        DateCreated: t('general_date_created'),
        Path: t('documents_path'),
        Modified: t('general_modified'),
        Created: t('general_created'),
        Location: t('general_location'),
        Type: t('panel_table_type'),
        Permission: t('general_permission'),
        Ascending: t('general_ascending'),
        Descending: t('general_descending'),
        None: t('general_none'),
        'View-LargeIcons': t('documents_view_large_icons'),
        'View-Details': t('documents_view_details'),
        Search: t('general_search'),
        'Button-Ok': t('general_button_ok'),
        'Button-Cancel': t('general_button_cancel'),
        'Button-Yes': t('general_button_yes'),
        'Button-No': t('general_button_cancel'),
        'Button-Create': t('general_button_create'),
        'Button-Save': t('general_button_save'),
        'Header-NewFolder': t('documents_header_folder'),
        'Content-NewFolder': t('documents_content_folder'),
        'Header-Rename': t('documents_rename'),
        'Content-Rename': t('documents_content_rename'),
        'Header-Rename-Confirmation': t('documents_header_rename_confirmation'),
        'Content-Rename-Confirmation': t('documents_content_rename_confirmation'),
        'Header-Delete': t('documents_header_delete'),
        'Content-Delete': t('documents_content_delete'),
        'Header-Multiple-Delete': t('documents_header_multiple_delete'),
        'Content-Multiple-Delete': t('documents_content_multiple_delete'),
        'Header-Folder-Delete': t('documents_header_folder_delete'),
        'Content-Folder-Delete': t('documents_content_folder_delete'),
        'Header-Duplicate': t('documents_header_duplicate'),
        'Content-Duplicate': t('documents_content_duplicate'),
        'Header-Upload': t('documents_header_upload'),
        Error: t('general_error'),
        'Validation-Empty': t('documents_validation_empty'),
        'Validation-Invalid': t('documents_validation_invalid'),
        'Validation-NewFolder-Exists': t('documents_validation_folder_exists'),
        'Validation-Rename-Exists': t('documents_validation_rename_exists'),
        'Folder-Empty': t('documents_folder_empty'),
        'File-Upload': t('documents_file_upload'),
        'Search-Empty': t('documents_search_empty'),
        'Search-Key': t('documents_search_key'),
        'Filter-Empty': t('documents_search_empty'),
        'Filter-Key': t('documents_filter_key'),
        'Sub-Folder-Error': t('documents_sub_folder_error'),
        'Same-Folder-Error': t('documents_same_folder_error'),
        'Access-Denied': t('documents_access_denied'),
        'Access-Details': t('documents_access_details'),
        'Header-Retry': t('documents_header_retry'),
        'Content-Retry': t('documents_content_retry'),
        'Button-Keep-Both': t('documents_button_keep_both'),
        'Button-Replace': t('documents_button_replace'),
        'Button-Skip': t('documents_button_skip'),
        'ApplyAll-Label': t('documents_apply_all_label'),
        KB: t('documents_KB'),
        'Access-Message': t('documents_access_message'),
        'Network-Error': t('documents_network_error'),
        'Server-Error': t('documents_server_error'),
      },
      uploader: {
        uploadSuccessMessage: t('document_success_upload'),
        uploadFailedMessage: t('document_fail_upload'),
        // invalidMinFileSize: '',
        invalidMaxFileSize: t('document_file_size_large'),
        invalidFileType: t('general_invalid_file_type'),
        // Browse: '',
        // Clear: '',
        // Upload: '',
        // dropFilesHint: '',
        // removedSuccessMessage: '',
        // removedFailedMessage: '',
        // inProgress: '',
        // readyToUploadMessage: '',
        remove: t('general_delete'),
        cancel: t('general_button_cancel'),
        delete: t('general_delete'),
      },
    },
  });

  useEffect(() => {
    window.addEventListener('load', () => {
      setIsLoading(false);
      setTimeout(() => setIsLoaded(true), 500);
    });
  }, []);

  const space = '';

  return (
    <Provider store={store}>
      <PersistGate loading={<MainSpinner />} persistor={persistor}>
        <BrowserRouter basename={`/${space}`}>
          <ScrollToTop>
            <>
              {!isLoaded && (
                <div className={`load${isLoading ? '' : ' loaded'}`}>
                  <div className="load__icon-wrap">
                    <svg className="load__icon">
                      <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                    </svg>
                  </div>
                </div>
              )}
              <div>
                <Router />
              </div>
            </>
          </ScrollToTop>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
};

export default App;
